import type { BoardItem } from './apiClient';

/**
 * "Is it one of these?": matching what somebody is typing against what is already on the board, in the browser (`docs/feedback-loop-plan.md`
 * §6.3). Tokenize, drop stopwords, strip plurals, weight each word by how rare it is across the board, and compare with cosine
 * similarity. No endpoint per keystroke and no database feature: the board is loaded once and is at most 200 items.
 *
 * ⚠️ KEYWORD MATCHING IS BLUNT, AND THE THRESHOLD COMES FROM REAL TEXT, NOT FROM A GUESS. It was run leave-one-out over all the feedback sent
 * so far (each message matched against all the others, as if it were being typed fresh), and at 0.40 only a minority of messages would
 * have been shown a suggestion. Reading those, a few pairs were real repeats of the same request; most of the rest were about the same
 * part of the app and asked something different (an export of one list against an export of another); none was unrelated. So expect
 * "related" more often than "the same", which is why every suggestion has **Not the same**. Lower the threshold and unrelated items start
 * to appear, which teaches people to ignore the panel; raise it and a rephrased repeat is missed. A missed match is cheap (the item is
 * sent, and an admin can merge it, in 3c); a wrong one costs somebody's attention every time, so this errs towards showing little. The
 * real text, and the counts, are not here: the numbers are in `docs/feedback-loop-plan.md` §6.8, and the specs use invented sentences
 * shaped like it.
 *
 * ⚠️ THE RATE GROWS WITH THE BOARD, SO AN EARLY, NEARLY EMPTY BOARD SHOWS LITTLE, NOT MUCH. Measured on random sub-boards of that text, a
 * suggestion was never shown on a board of one or two items, rarely on one of a handful, and about as often as above only on a full board.
 * With so few items to compare, rarity weighting has little to go on, but real messages are long and varied enough that two of them seldom
 * clear 0.40 by chance. (A SHORT pair that share most of their words do clear it even on a board of one: "Add an export to Excel button on
 * the project list page" against "Add an export button for the equipment list page" scores about 0.67. The threshold is tuned for
 * messages as long as people actually write them.)
 */

/** The least similarity (cosine, 0 to 1) at which an item is suggested. */
export const SUGGEST_THRESHOLD = 0.4;
/** The most that are suggested at once. */
export const SUGGEST_MAX = 3;
/** The fewest DISTINCT meaningful words somebody must have typed before anything is suggested: a few words match almost anything. */
export const SUGGEST_MIN_WORDS = 5;
/**
 * The fewest DISTINCT meaningful words a board item must have to be suggested at all. The same reason from the other side: an item that says
 * "Export" is contained in every longer message about exports, and scores very high against all of them (cosine against a single word is
 * just how much of the message that word is).
 */
export const SUGGEST_MIN_DOC_WORDS = 3;

/** Words that carry no meaning of their own. Generic English only: words common on this board ("project", "add") are down-weighted by how often they appear, not listed here. */
const STOPWORDS = new Set((
  'a about above after again against all also am an and any are aren as at be because been before being below between both but by can cannot could ' +
  'couldn did didn do does doesn doing don down during each few for from further had hadn has hasn have haven having he her here hers him his how i ' +
  'if in into is isn it its itself just let me more most my myself no nor not now of off on once only or other our ours out over own same shall she ' +
  'should shouldn so some such than that the their theirs them then there these they this those through to too under until up us very was wasn we ' +
  'were weren what when where which while who whom why will with won would wouldn you your yours yourself ' +
  // What is left of a contraction once the apostrophe splits it ("we're", "you've", "they'll", "mustn't"): not words.
  're ve ll mustn needn mightn shan ' +
  // Filler that opens most requests and says nothing about which one it is.
  'please thanks thank hi hello hey really maybe also still get got able want wants need needs like would one two'
).split(/\s+/));

/** Plurals the rule below gets wrong, and that turn up in software feedback often enough to matter. A short list, not an attempt at a stemmer. */
const PLURALS = new Map([
  ['caches', 'cache'], ['niches', 'niche'], ['statuses', 'status'], ['menus', 'menu'], ['cookies', 'cookie'], ['movies', 'movie'],
  // Too short, or ending in -is, for the general rule: lower-case "pos", "ids", "rfis". (Capitalized, "POs", "IDs", "RFIs" are read by `unplural`.)
  ['pos', 'po'], ['ids', 'id'], ['apis', 'api'], ['rfis', 'rfi'], ['uis', 'ui'],
]);

/** "RFIs", "POs", "PDFs", "IDs": a run of capitals and digits and then a lower-case s is an acronym in the plural, whatever the word. */
const unplural = (word: string): string => (/^[\p{Lu}\p{N}]{2,}s$/u.test(word) ? word.slice(0, -1) : word);

/**
 * Drops a plural ending, conservatively: "reports" → "report", "entries" → "entry", "boxes" → "box", "sizes" → "size", and leaves
 * "status", "address", "access" alone. Where it is wrong the result is a missed match, which is the cheap kind of wrong.
 */
function stem(word: string): string {
  const known = PLURALS.get(word);
  if (known) return known;
  if (word.length > 4 && word.endsWith('ies')) return `${word.slice(0, -3)}y`;
  if (word.length > 4 && /(?:ss|x|zz|ch|sh)es$/.test(word)) return word.slice(0, -2);
  if (word.length > 3 && word.endsWith('s') && !/(?:ss|us|is)$/.test(word)) return word.slice(0, -1);
  return word;
}

/** The meaningful words of some text, in order and with repeats: lower-cased, letters and digits only, no stopwords, plurals stripped. */
export function tokens(text: string): string[] {
  const words = (text.match(/[\p{L}\p{N}]+/gu) ?? []).map((w) => unplural(w).toLowerCase());
  return words.filter((w) => w.length > 1 && !STOPWORDS.has(w)).map(stem).filter((w) => !STOPWORDS.has(w));
}

/** Whether these words are enough to match on: `minWords` DIFFERENT ones. */
const enough = (words: readonly string[], minWords: number): boolean => new Set(words).size >= minWords;

interface Doc { item: BoardItem; weights: Map<string, number>; norm: number; distinct: number }
export interface SimilarIndex { docs: readonly Doc[]; idf: ReadonlyMap<string, number> }
export interface Suggestion { item: BoardItem; score: number }

const idfOf = (n: number, df: number): number => Math.log((n + 1) / (df + 1)) + 1;

/** Sublinear term frequency: saying a word ten times is not ten times as much about it. */
function weigh(words: readonly string[], idf: (w: string) => number): { weights: Map<string, number>; norm: number } {
  const counts = new Map<string, number>();
  for (const w of words) counts.set(w, (counts.get(w) ?? 0) + 1);
  const weights = new Map<string, number>();
  let sumSquares = 0;
  for (const [w, c] of counts) {
    const weight = (1 + Math.log(c)) * idf(w);
    weights.set(w, weight);
    sumSquares += weight * weight;
  }
  return { weights, norm: Math.sqrt(sumSquares) };
}

/** Builds what matching needs from the board, once per load of it. */
export function buildIndex(items: readonly BoardItem[]): SimilarIndex {
  const tokenized = items.map((item) => ({ item, words: tokens(item.message) }));
  const df = new Map<string, number>();
  for (const { words } of tokenized) for (const w of new Set(words)) df.set(w, (df.get(w) ?? 0) + 1);
  const n = items.length;
  const idf = new Map<string, number>([...df].map(([w, d]) => [w, idfOf(n, d)]));
  // Every word of every item was counted into `df`, so each has an idf here.
  const docs = tokenized.map(({ item, words }) => ({ item, ...weigh(words, (w) => idf.get(w) as number), distinct: new Set(words).size }));
  return { docs, idf };
}

/**
 * The items most like `text`, best first: at most `SUGGEST_MAX`, each at least `SUGGEST_THRESHOLD` similar, and nothing at all until
 * there is enough to go on. `skip` is the ids somebody said are "not the same", so a dismissed item does not come back as they type.
 * Ties go to the newer item (the higher id), so the order does not depend on how the board happened to be sorted.
 */
export function suggest(index: SimilarIndex, text: string, skip: ReadonlySet<number> = new Set()): Suggestion[] {
  return suggestFor(index, text, { threshold: SUGGEST_THRESHOLD, max: SUGGEST_MAX, minWords: SUGGEST_MIN_WORDS }, skip);
}

/** What `suggestFor` asks of a match: how similar, how many, and how many different words the text needs before anything is said. */
export interface SuggestOptions { threshold: number; max: number; minWords: number }

/**
 * `suggest` with its limits chosen by the caller. The members' suggestions use `suggest` (the strict limits above, because a wrong one costs somebody's
 * attention every time); the Admin's merge picker uses looser ones, because an admin confirms each (`feedbackMerge.ts`). The index and the scoring are the same.
 */
export function suggestFor(index: SimilarIndex, text: string, options: SuggestOptions, skip: ReadonlySet<number> = new Set()): Suggestion[] {
  const words = tokens(text);
  if (!enough(words, options.minWords) || index.docs.length === 0) return [];
  const query = weigh(words, (w) => index.idf.get(w) ?? idfOf(index.docs.length, 0));
  if (query.norm === 0) return [];
  const found: Suggestion[] = [];
  for (const doc of index.docs) {
    if (skip.has(doc.item.id) || doc.norm === 0 || doc.distinct < SUGGEST_MIN_DOC_WORDS) continue;
    let dot = 0;
    for (const [w, weight] of query.weights) dot += weight * (doc.weights.get(w) ?? 0);
    const score = dot / (query.norm * doc.norm);
    if (score >= options.threshold) found.push({ item: doc.item, score });
  }
  return found.sort((a, b) => b.score - a.score || b.item.id - a.item.id).slice(0, options.max);
}
